<template>
  <div class="blog-page">
    <BlogHeaderMobile
      :query="searchTerm"
      @search="applySearch"
    />

    <BlogHero :can-publish="canPublish" @publish="openComposer()" />

    <BlogSavedBar :query="searchTerm" @search="applySearch" />

    <main class="blog-main">
      <BlogFilters
        :categories="categories"
        :active-category="activeCategory"
      />

      <div class="blog-content">
        <template v-if="!isFiltered">
          <section v-if="featured.length" class="blog-featured" aria-labelledby="blog-featured-title">
            <div class="blog-section-heading">
              <v-icon class="blog-section-heading__icon" aria-hidden="true">mdi-fire</v-icon>
              <h2 id="blog-featured-title" class="blog-section-title">Em destaque</h2>
              <span class="blog-section-heading__line" aria-hidden="true"></span>
            </div>
            <div class="blog-featured__grid">
              <article v-for="(post, index) in featured" :key="post.id" class="blog-featured__item">
                <BlogPostCard :post="post" :rank="index + 1" />
              </article>
            </div>
          </section>
        </template>

        <section class="blog-latest" :aria-labelledby="listTitleId">
          <div class="blog-section-heading">
            <h2 :id="listTitleId" class="blog-section-title">{{ listTitle }}</h2>
            <span class="blog-section-heading__line" aria-hidden="true"></span>
          </div>

          <div v-if="loading" class="blog-state" aria-live="polite">
            <v-progress-circular indeterminate color="#ffcf5a" />
            <p>Carregando matérias…</p>
          </div>

          <div v-else-if="loadError" class="blog-state blog-state--error" role="alert">
            <v-icon large color="#f25568">mdi-alert-circle-outline</v-icon>
            <p>{{ loadError }}</p>
            <v-btn outlined color="#ffcf5a" @click="loadPosts(true)">Tentar novamente</v-btn>
          </div>

          <div v-else-if="!visibleLatest.length" class="blog-state">
            <v-icon large color="rgba(255, 255, 255, 0.4)">mdi-newspaper-variant-outline</v-icon>
            <p v-if="searchTerm" class="blog-state__message">
              Nenhuma matéria encontrada para “{{ searchTerm }}”.
            </p>
            <p v-else-if="activeCategory" class="blog-state__message">
              Ainda não há matérias em {{ activeCategoryName }}.
            </p>
            <p v-else class="blog-state__message">Ainda não há matérias publicadas.</p>
            <button v-if="isFiltered" type="button" class="blog-state__clear" @click="clearFilters">
              Ver todas as matérias
            </button>
          </div>

          <div v-else class="blog-latest__grid">
            <BlogPostCard v-for="post in visibleLatest" :key="post.id" :post="post" />
          </div>

          <div v-if="loadingMore" class="blog-state blog-state--inline" aria-live="polite">
            <v-progress-circular indeterminate size="24" color="#ffcf5a" />
            <p>Carregando mais…</p>
          </div>

          <button
            v-if="hasMore && !loading && !loadError"
            type="button"
            class="blog-load-more"
            :disabled="loadingMore"
            @click="loadPosts(false)"
          >
            {{ loadingMore ? "Carregando…" : "Carregar mais matérias" }}
          </button>
        </section>

        <aside
          v-if="curiosities.length"
          class="blog-curiosities"
          aria-labelledby="blog-curiosities-title"
        >
          <div class="blog-section-heading">
            <h2 id="blog-curiosities-title" class="blog-section-title">Curiosidades rápidas</h2>
            <span class="blog-section-heading__line" aria-hidden="true"></span>
          </div>
          <ul class="blog-curiosities__list">
            <li v-for="post in curiosities" :key="post.id" class="blog-curiosities__item">
              <router-link :to="{ name: 'BlogPost', params: { slug: post.slug } }" class="blog-curiosities__link">
                <span class="blog-curiosities__title">{{ post.title || "Matéria sem título" }}</span>
                <time class="blog-curiosities__date" :datetime="post.created_at">{{ shortDate(post.created_at) }}</time>
              </router-link>
            </li>
          </ul>
        </aside>

        <section v-if="!isFiltered" class="blog-updates" aria-labelledby="blog-updates-title">
          <div class="blog-section-heading">
            <h2 id="blog-updates-title" class="blog-section-title">Últimas atualizações</h2>
            <span class="blog-section-heading__line" aria-hidden="true"></span>
          </div>

          <div v-if="updatesLoading" class="blog-state" aria-live="polite">
            <v-progress-circular indeterminate color="#ffcf5a" />
            <p>Carregando atualizações…</p>
          </div>

          <div v-else-if="updatesError" class="blog-state blog-state--error" role="alert">
            <v-icon large color="#f25568">mdi-alert-circle-outline</v-icon>
            <p>{{ updatesError }}</p>
            <v-btn outlined color="#ffcf5a" @click="loadUpdates(true)">Tentar novamente</v-btn>
          </div>

          <div v-else-if="!updates.length" class="blog-state">
            <v-icon large color="rgba(255, 255, 255, 0.4)">mdi-update</v-icon>
            <p class="blog-state__message">Ainda não há atualizações publicadas.</p>
          </div>

          <div v-else class="blog-updates__grid">
            <BlogPostCard v-for="post in updates" :key="post.id" :post="post" />
          </div>

          <div v-if="updatesLoadingMore" class="blog-state blog-state--inline" aria-live="polite">
            <v-progress-circular indeterminate size="24" color="#ffcf5a" />
            <p>Carregando mais…</p>
          </div>

          <button
            v-if="updatesHasMore && !updatesLoading && !updatesError"
            type="button"
            class="blog-load-more"
            :disabled="updatesLoadingMore"
            @click="loadUpdates(false)"
          >
            {{ updatesLoadingMore ? "Carregando…" : "Carregar mais atualizações" }}
          </button>
        </section>
      </div>
    </main>

    <BlogComposer
      v-model="composerOpen"
      :post="editingPost"
      :submitting="submitting"
      :error="composerError"
      @submit="submitPost"
    />

    <v-snackbar v-model="snackbar" color="#31263a" bottom :timeout="5000">
      {{ message }}
      <template #action="{ attrs }">
        <v-btn text color="#f6c945" v-bind="attrs" @click="snackbar = false">Fechar</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import BlogComposer from "@/components/blog/BlogComposer.vue";
import BlogFilters from "@/components/blog/BlogFilters.vue";
import BlogHeaderMobile from "@/components/blog/BlogHeaderMobile.vue";
import BlogHero from "@/components/blog/BlogHero.vue";
import BlogPostCard from "@/components/blog/BlogPostCard.vue";
import BlogSavedBar from "@/components/blog/BlogSavedBar.vue";
import { createBlogPost, getBlogCategories, getBlogFeaturedPosts, getBlogPosts, blogErrorMessage } from "@/server/BlogService";
import { fetchSession } from "@/server/authServe";
import { hasAuth } from "@/server/authStorage";
import { syncSessionIdentity } from "@/utils/storeUserData";
import { canPublishBlog } from "@/utils/blogPermissions";
import "@/styles/blog-page.css";

const LIST_LIMIT = 12;

export default {
  name: "BlogView",
  components: {
    BlogComposer,
    BlogFilters,
    BlogHeaderMobile,
    BlogHero,
    BlogPostCard,
    BlogSavedBar,
  },
  data: () => ({
    categories: [],
    categoriesLoading: false,
    categoriesError: false,
    featured: [],
    latest: [],
    curiosities: [],
    cursor: null,
    hasMore: false,
    loading: false,
    loadingMore: false,
    loadError: "",
    updates: [],
    updatesCursor: null,
    updatesHasMore: false,
    updatesLoading: false,
    updatesLoadingMore: false,
    updatesError: "",
    composerOpen: false,
    editingPost: null,
    submitting: false,
    composerError: "",
    snackbar: false,
    message: "",
    sessionReady: false,
  }),
  computed: {
    authenticated() {
      return hasAuth();
    },
    canPublish() {
      return this.sessionReady && canPublishBlog();
    },
    activeCategory() {
      return String(this.$route.query.categoria || "");
    },
    searchTerm() {
      return String(this.$route.query.q || "");
    },
    isFiltered() {
      return Boolean(this.activeCategory || this.searchTerm);
    },
    activeCategoryName() {
      const category = this.categories.find((item) => item.slug === this.activeCategory);
      return category ? category.name : this.activeCategory;
    },
    listTitle() {
      if (this.searchTerm) return `Resultados para “${this.searchTerm}”`;
      if (this.activeCategory) return this.activeCategoryName;
      return "Últimas notícias";
    },
    listTitleId() {
      return this.isFiltered ? "blog-results-title" : "blog-latest-title";
    },
    featuredIds() {
      return new Set(this.featured.map((item) => item.id));
    },
    visibleLatest() {
      if (this.isFiltered) return this.latest;
      return this.latest.filter((item) => !this.featuredIds.has(item.id));
    },
  },
  created() {
    this.syncPermissionIdentity();
    this.loadCategories();
    this.loadCuriosities();
    this.loadPosts(true);
    this.loadUpdates(true);
  },
  beforeDestroy() {
    document.title = "Zinnes";
  },
  methods: {
    async syncPermissionIdentity() {
      if (!this.authenticated) {
        this.sessionReady = true;
        return;
      }

      try {
        const session = await fetchSession();
        syncSessionIdentity(session);
      } catch {
        // Falha transitória: preserva a identidade local apenas para
        // apresentação; a API de publicação continua sendo a autoridade.
      } finally {
        this.sessionReady = true;
      }
    },
    async loadCategories() {
      this.categoriesLoading = true;
      this.categoriesError = false;
      try {
        const body = await getBlogCategories();
        this.categories = body.data?.items || [];
      } catch {
        this.categoriesError = true;
      } finally {
        this.categoriesLoading = false;
      }
    },
    async loadCuriosities() {
      try {
        const body = await getBlogPosts({ category: "curiosidades", limit: 4 });
        this.curiosities = body.data?.items || [];
      } catch {
        this.curiosities = [];
      }
    },
    async loadFeatured() {
      try {
        const body = await getBlogFeaturedPosts();
        this.featured = body.data?.items || [];
      } catch {
        this.featured = [];
      }
    },
    async loadUpdates(reset = true) {
      if (reset) {
        this.updatesLoading = true;
        this.updatesError = "";
        this.updates = [];
        this.updatesCursor = null;
        this.updatesHasMore = false;
      } else {
        this.updatesLoadingMore = true;
      }

      try {
        const body = await getBlogPosts({
          category: "atualizacoes",
          query: null,
          cursor: reset ? null : this.updatesCursor,
          limit: LIST_LIMIT,
        });
        const page = body.data;
        const items = page.items || [];
        if (reset) {
          this.updates = items;
        } else {
          const map = new Map(this.updates.map((item) => [item.id, item]));
          items.forEach((item) => map.set(item.id, item));
          this.updates = Array.from(map.values());
        }
        this.updatesCursor = page.next_cursor;
        this.updatesHasMore = Boolean(page.has_more);
      } catch (error) {
        if (reset) this.updatesError = blogErrorMessage(error, "Não foi possível carregar as atualizações.");
        else this.notify(blogErrorMessage(error, "Não foi possível carregar mais atualizações."));
      } finally {
        this.updatesLoading = false;
        this.updatesLoadingMore = false;
      }
    },
    async loadPosts(reset = true) {
      if (reset) {
        this.loading = true;
        this.loadError = "";
        this.latest = [];
        this.cursor = null;
        this.hasMore = false;
        this.applyMeta();
        if (!this.isFiltered) this.loadFeatured();
      } else {
        this.loadingMore = true;
      }

      const term = this.searchTerm;
      if (term && (term.length < 3 || term.length > 80)) {
        this.latest = [];
        this.loading = false;
        this.loadingMore = false;
        return;
      }

      try {
        const body = await getBlogPosts({
          category: this.activeCategory || null,
          query: term || null,
          cursor: reset ? null : this.cursor,
          limit: LIST_LIMIT,
        });
        const page = body.data;
        const items = page.items || [];
        if (reset) {
          this.latest = items;
        } else {
          const map = new Map(this.latest.map((item) => [item.id, item]));
          items.forEach((item) => map.set(item.id, item));
          this.latest = Array.from(map.values());
        }
        this.cursor = page.next_cursor;
        this.hasMore = Boolean(page.has_more);
      } catch (error) {
        if (reset) this.loadError = blogErrorMessage(error, "Não foi possível carregar as matérias.");
        else this.notify(blogErrorMessage(error, "Não foi possível carregar mais matérias."));
      } finally {
        this.loading = false;
        this.loadingMore = false;
      }
    },
    applyMeta() {
      const base = "Portal definitivo do fã | Portal Zinnes";
      document.title = this.isFiltered ? `${this.listTitle} | Portal Zinnes` : base;
      const description = this.isFiltered
        ? `Matérias do Portal Zinnes ${this.listTitle}.`
        : "Seu Portal de Notícias Nacionais! Descubra notícias, histórias e seu próximo personagem nacional favorito — Aqui você acompanha tudo o que acontece no cenário geek brasileiro.";
      this.ensureMeta("description", description);
    },
    ensureMeta(name, content) {
      let meta = document.head.querySelector(`meta[name="${name}"]`);
      if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute("name", name);
        document.head.appendChild(meta);
      }
      meta.setAttribute("content", content);
    },
    applySearch(term) {
      const value = String(term || "").trim();
      if (value.length >= 3 || value.length === 0) {
        const query = { ...this.$route.query };
        if (value) query.q = value;
        else delete query.q;
        this.$router.push({ path: "/portal-zinnes", query });
      }
    },
    clearFilters() {
      this.$router.push({ path: "/portal-zinnes" });
    },
    shortDate(value) {
      const date = new Date(value);
      return Number.isNaN(date.getTime())
        ? ""
        : new Intl.DateTimeFormat("pt-BR", { day: "2-digit", month: "short", year: "numeric" }).format(date);
    },
    requireAuth() {
      if (this.authenticated) return true;
      this.$router.push({ path: "/login", query: { redirect: this.$route.fullPath } });
      return false;
    },
    openComposer(post = null) {
      if (!this.requireAuth()) return;
      this.editingPost = post;
      this.composerError = "";
      this.composerOpen = true;
    },
    async submitPost(payload) {
      this.submitting = true;
      this.composerError = "";
      try {
        const body = await createBlogPost({
          title: payload.title,
          content: payload.content,
          categoryId: payload.categoryId,
          images: payload.images,
        });
        const post = body.data?.post;
        this.composerOpen = false;
        this.editingPost = null;
        this.notify("Matéria publicada com sucesso.");
        if (post?.slug) {
          this.$router.push({ name: "BlogPost", params: { slug: post.slug } });
        } else {
          this.loadPosts(true);
        }
      } catch (error) {
        this.composerError = blogErrorMessage(error, "Não foi possível publicar a matéria.");
      } finally {
        this.submitting = false;
      }
    },
    notify(message) {
      this.message = message;
      this.snackbar = true;
    },
  },
  watch: {
    "$route.query.categoria"() {
      this.loadPosts(true);
    },
    "$route.query.q"() {
      this.loadPosts(true);
    },
  },
};
</script>
